import type { StarterDashboardV2PageData } from '#src/app/pages/starterDashboardV2/pageContext';
import { useStarterDashboardV2PageData } from '#src/app/pages/starterDashboardV2/pageContext';
import { useFreePromotionStatus } from '#src/app/pages/starterDashboardV2/shared/hooks';

import { NOTIFICATION_TYPES } from '../../../constants';

const SIX_HOURS = 6 * 60 * 60 * 1000;
const ONE_DAY = 24 * 60 * 60 * 1000;

type FreePromotions = NonNullable<StarterDashboardV2PageData['petition']['freePromotions']>;
function shouldShowRecap(promotions: FreePromotions) {
	// If there are no promotions, don't show the notification
	if (promotions.length === 0) return false;

	// If there is an existing starter free promotion, don't show the notification
	const starterFreePromotion = promotions.find((promotion) => promotion.sourceLocation === 'starter_free_promotion');
	if (starterFreePromotion) return false;

	// If there is no auto free promotion (includes kickstart: auto_free_promotion_signature_milestone_5), don't show the notification
	const autoFreePromotion = promotions.find((promotion) => promotion.sourceLocation?.startsWith('auto_free_promotion'));
	if (!autoFreePromotion) return false;

	// If the auto free promotion has not been fulfilled, don't show the notification
	const isFulfilled = !!autoFreePromotion.fulfilledAt;
	if (!isFulfilled) return false;

	// If the auto free promotion has been fulfilled less than 6 hours ago, don't show the notification
	const now = new Date();
	const fulfilledAtDate = new Date(autoFreePromotion.fulfilledAt);
	const isSixHoursAfterFulfillment = now.getTime() - fulfilledAtDate.getTime() >= SIX_HOURS;
	if (!isSixHoursAfterFulfillment) return false;

	// If the auto free promotion has been fulfilled more than 30 hours ago, don't show the notification
	const isOneDayAfterFulfillment = now.getTime() - fulfilledAtDate.getTime() >= ONE_DAY + SIX_HOURS;
	if (isOneDayAfterFulfillment) return false;

	// If the auto free promotion has less than 2 signatures, don't show the notification
	const signatures = autoFreePromotion.stats?.signatureCount ?? 0;
	if (signatures <= 1) return false;

	return true;
}

type UseFreePromotionFulfillmentReturn = { type: (typeof NOTIFICATION_TYPES)['freePromotionFulfillment'] } | undefined;

export function useFreePromotionFulfillment(): UseFreePromotionFulfillmentReturn {
	const { data } = useStarterDashboardV2PageData();

	const promotions = data.petition.freePromotions ?? [];
	const autoFreePromotion = promotions.find((promotion) => promotion.sourceLocation?.startsWith('auto_free_promotion'));
	const shouldShowNotification = shouldShowRecap(promotions);

	// shows a banner during processing of the auto free promotion
	useFreePromotionStatus(autoFreePromotion);

	// ALERTS: comment this line
	if (!shouldShowNotification) return undefined;

	return { type: NOTIFICATION_TYPES.freePromotionFulfillment };
}
